/* ─── <variant-swatches> ─────────────────────────────────────────────────────
   PDP: wraps colour swatch wrappers for a single option.
   On click → updates the hidden <select> → dispatches change event →
   slate.Variants handles the rest (price, image, availability).
   Active ring only shown on available swatches.
   ─────────────────────────────────────────────────────────────────────────── */

if (!customElements.get('variant-swatches')) {
  customElements.define(
    'variant-swatches',
    class VariantSwatches extends HTMLElement {
      connectedCallback() {
        this._select = this.nextElementSibling;
        this._labelValue = this.closest('.product-form__item').querySelector('.swatch-label-value');
        this._swatches = this.querySelectorAll('.color-swatch-wrap');

        this._handleClick = this._onSwatchClick.bind(this);
        this._handleKeydown = this._onSwatchKeydown.bind(this);

        this._swatches.forEach(swatch => {
          swatch.addEventListener('click', this._handleClick);
          swatch.addEventListener('keydown', this._handleKeydown);
        });
      }

      disconnectedCallback() {
        if (this._swatches && this._handleClick) {
          this._swatches.forEach(swatch => {
            swatch.removeEventListener('click', this._handleClick);
            swatch.removeEventListener('keydown', this._handleKeydown);
          });
        }
      }

      _onSwatchClick(event) {
        this._selectSwatch(event.currentTarget);
      }

      _onSwatchKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._selectSwatch(event.currentTarget);
        }
      }

      _selectSwatch(swatch) {
        const value = swatch.dataset.value;
        const isUnavailable = swatch.classList.contains('is-unavailable');

        if (this._select) {
          this._select.value = value;
          this._select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this._labelValue) {
          this._labelValue.textContent = value;
        }

        this._setActive(swatch);
      }

      _setActive(activeSwatch) {
        this._swatches.forEach(s => {
          s.classList.remove('is-active');
          s.setAttribute('aria-checked', 'false');
        });
        activeSwatch.classList.add('is-active');
        activeSwatch.setAttribute('aria-checked', 'true');
      }
    }
  );
}

/* ─── <card-color-swatches> ──────────────────────────────────────────────────
   PLP: wraps colour swatch wrappers on a product card.
   On click → swaps card image → updates card link href to ?variant=id.
   Active ring only shown on available swatches.
   On connectedCallback: fixes initial active state to first available variant.
   ─────────────────────────────────────────────────────────────────────────── */

if (!customElements.get('card-color-swatches')) {
  customElements.define(
    'card-color-swatches',
    class CardColorSwatches extends HTMLElement {
      connectedCallback() {
        this._card = this.closest('.grid-view-item');
        this._cardLink = this._card && this._card.querySelector('.grid-view-item__link');
        this._swatches = this.querySelectorAll('.color-swatch-wrap');

        this._handleClick = this._onSwatchClick.bind(this);
        this._handleKeydown = this._onSwatchKeydown.bind(this);

        this._swatches.forEach(swatch => {
          swatch.addEventListener('click', this._handleClick);
          swatch.addEventListener('keydown', this._handleKeydown);
        });

        // Fix initial active state: use first available variant, not just first swatch
        this._initActiveState();
      }

      disconnectedCallback() {
        if (this._swatches && this._handleClick) {
          this._swatches.forEach(swatch => {
            swatch.removeEventListener('click', this._handleClick);
            swatch.removeEventListener('keydown', this._handleKeydown);
          });
        }
      }

      _onSwatchClick(event) {
        event.stopPropagation();
        this._selectSwatch(event.currentTarget);
      }

      _onSwatchKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          this._selectSwatch(event.currentTarget);
        }
      }

      _selectSwatch(swatch) {
        const variantId = swatch.dataset.variantId;
        const variantImage = swatch.dataset.variantImage;
        const isUnavailable = swatch.classList.contains('is-unavailable');

        this._swapImage(variantImage, true);

        if (variantId && this._cardLink) {
          const baseUrl = this._cardLink.href.split('?')[0];
          this._cardLink.href = baseUrl + '?variant=' + variantId;
        }

        this._setActive(swatch);
      }

      _swapImage(variantImage, hideHover) {
        if (!variantImage || !this._card) return;

        const wrapper = this._card.querySelector('.grid-view-item__image-wrapper');
        if (!wrapper) return;

        // Check if we already show this exact variant
        const existing = wrapper.querySelector('.cs-variant-img');
        const existingSrc = existing ? existing.getAttribute('data-swap-src') : null;

        if (existingSrc !== variantImage) {
          // Create new overlay invisible — shown only after load to avoid flash
          const img = document.createElement('img');
          img.className = 'cs-variant-img';
          img.setAttribute('data-swap-src', variantImage);
          img.alt = '';
          img.setAttribute('aria-hidden', 'true');
          img.style.opacity = '0';
          wrapper.appendChild(img);

          img.onload = () => {
            if (existing) existing.remove();
            img.style.opacity = '';
          };
          img.onerror = () => {
            img.remove(); // failed to load — keep existing
          };

          img.src = variantImage;
        }

        if (hideHover) {
          const hoverImg = this._card.querySelector('.product-card__hover-image');
          if (hoverImg) hoverImg.style.display = 'none';
          this._card.classList.remove('product-card--has-hover-image');
        }
      }

      _setActive(activeSwatch) {
        this._swatches.forEach(s => {
          s.classList.remove('is-active');
          s.setAttribute('aria-checked', 'false');
        });
        activeSwatch.classList.add('is-active');
        activeSwatch.setAttribute('aria-checked', 'true');
      }

      _initActiveState() {
        const swatches = Array.from(this._swatches);
        if (!swatches.length) return;

        // Trust SSR is-active (Liquid set it to first-image variant); fall back to first swatch
        const targetSwatch = swatches.find(s => s.classList.contains('is-active')) || swatches[0];

        // No image swap on init — card already shows product.featured_media (position-1 image) via lazysizes.
        // Only swap when user explicitly clicks a swatch.
        if (targetSwatch.dataset.variantId && this._cardLink) {
          const baseUrl = this._cardLink.href.split('?')[0];
          this._cardLink.href = baseUrl + '?variant=' + targetSwatch.dataset.variantId;
        }
      }
    }
  );
}
